import React, {
  useEffect,
  useState,
  useCallback,
  useRef,
  useMemo,
} from "react";
import { useFieldArray, useFormContext } from "react-hook-form";
import { Button, Row, Col, Badge } from "reactstrap";
import { Table, Drawer, Space, Popover } from "antd";
import { EditOutlined, DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import FormSelect from "../../form-components/SelectComponent";
import FormInput from "../../form-components/InputComponent";
import { Icon } from "../../../components/Component";
import supplierApis from "../../../api/master/supplier";
import materialsApis from "../../../api/master/materials";
import sitebreakdownMasterApis from "../../../api/master/sitebreakdownMaster";
import BOQMaterialDrawer from "./BOQMaterialDrawer";
import AddBOQLineModal from "./AddBOQLineModal";
import Swal from "sweetalert2";

interface SelectOptions {
  label: string;
  value: string;
}

const HIERARCHY_UNIT_TITLES: Record<string, string> = {
  TOWER_BLOCK: "Tower / Block",
  VILLA: "Villa",
  FLOOR: "Tower / Block",
  FLAT: "Tower / Block",
};

const ConsumptionTarget_Materails: React.FC<any> = (props: any) => {
  const { handleSubmit, watch, setValue, getValues, control } =
    useFormContext();

  const [materialCategoryOptions, setMaterialCategoryOptions] = useState([]);
  const [categoryOptions, setCategoryOptions] = useState<SelectOptions[]>([]);
  const [materialOptions, setMaterialOptions] = useState<SelectOptions[]>([]);
  const [showAddMaterialDrawer, setShowAddMaterialDrawer] = useState(false);
  const [showAddBOQLineModal, setShowAddBOQLineModal] = useState(false);
  const [currentEditMaterial, setCurrentEditMaterial] = useState<any>(null);
  const [currentEditBOQLine, setCurrentEditBOQLine] = useState<any>(null);
  const [selectedBOQLineIndex, setSelectedBOQLineIndex] = useState<
    number | null
  >(null);
  const [expandedRowKeys, setExpandedRowKeys] = useState<string[]>([]);

  // Location options for table display
  const [locationOptions, setLocationOptions] = useState<SelectOptions[]>([]);
  const [towerOptions, setTowerOptions] = useState<SelectOptions[]>([]);
  const [flatOptions, setFlatOptions] = useState<SelectOptions[]>([]);

  // Track previous workLevelSetting and validation errors
  const previousWorkLevelSetting = useRef<string | undefined>(undefined);
  const [boqLineErrors, setBoqLineErrors] = useState<Record<number, string[]>>(
    {}
  );

  const {
    fields: boqLines,
    append: appendBOQLine,
    remove: removeBOQLine,
    replace: replaceBOQLines,
  } = useFieldArray({
    control,
    name: "boqLines",
  });

  const previousFormDate = watch();
  const blockArea = previousFormDate?.blockArea || 1;
  const workLevelSetting = watch("workLevelSetting");
  const siteId = watch("siteId");

  // Function to validate BOQ line based on workLevelSetting
  const validateBoqLine = (boqLine: any, index: number): string[] => {
    const errors: string[] = [];

    if (!workLevelSetting) {
      return errors;
    }

    // Required fields based on hierarchy
    if (workLevelSetting === "TOWER_BLOCK" || workLevelSetting === "VILLA") {
      if (!boqLine.siteStructureUnitId) {
        errors.push(
          `${
            workLevelSetting === "VILLA" ? "Villa" : "Tower / Block"
          } is required`
        );
      }
    } else if (workLevelSetting === "FLOOR") {
      if (!boqLine.siteStructureUnitId) {
        errors.push("Tower / Block is required");
      }
      if (!boqLine.floorNo) {
        errors.push("Floor is required");
      }
    } else if (workLevelSetting === "FLAT") {
      if (!boqLine.siteStructureUnitId) {
        errors.push("Tower / Block is required");
      }
      if (!boqLine.floorNo) {
        errors.push("Floor is required");
      }
      // if (!boqLine.siteStructureSubUnitId) {
      //   errors.push("Flat / Unit is required");
      // }
    }

    return errors;
  };

  // Validate all BOQ lines when workLevelSetting changes
  useEffect(() => {
    if (
      workLevelSetting &&
      previousWorkLevelSetting.current !== undefined &&
      previousWorkLevelSetting.current !== workLevelSetting &&
      boqLines.length > 0
    ) {
      // Work level setting changed, validate all existing BOQ lines
      const errors: Record<number, string[]> = {};
      boqLines.forEach((_, index) => {
        const boqLine =
          getValues(`boqLines.${index}`) || watch(`boqLines.${index}`);
        const lineErrors = validateBoqLine(boqLine, index);
        if (lineErrors.length > 0) {
          errors[index] = lineErrors;
        }
      });
      setBoqLineErrors(errors);

      // Show warning if there are errors
      if (Object.keys(errors).length > 0) {
        Swal.fire({
          icon: "warning",
          title: "Site Hierarchy Changed",
          html: `Site hierarchy has been changed. Please update the following BOQ lines with required fields:<br/><br/>${Object.entries(
            errors
          )
            .map(
              ([index, errs]) =>
                `BOQ Line ${Number(index) + 1}: ${errs.join(", ")}`
            )
            .join("<br/>")}`,
          confirmButtonText: "OK",
        });
      }
    }
    previousWorkLevelSetting.current = workLevelSetting;
  }, [workLevelSetting, boqLines.length]);

  // Re-validate BOQ lines when they are updated (watch for changes in BOQ line fields)
  // Watch all BOQ line fields that affect validation
  const watchedBoqLines = boqLines.map((_, idx) => watch(`boqLines.${idx}`));
  const boqLineValidationKeys = useMemo(() => {
    return watchedBoqLines
      .map((line) => {
        return `${line?.siteStructureUnitId || ""}-${line?.floorNo || ""}-${
          line?.siteStructureSubUnitId || ""
        }`;
      })
      .join("||");
  }, [watchedBoqLines]);

  useEffect(() => {
    if (workLevelSetting && boqLines.length > 0) {
      const errors: Record<number, string[]> = {};
      boqLines.forEach((_, index) => {
        const boqLine = watch(`boqLines.${index}`);
        if (boqLine) {
          const lineErrors = validateBoqLine(boqLine, index);
          if (lineErrors.length > 0) {
            errors[index] = lineErrors;
          } else {
            // Clear errors if line is now valid
            setBoqLineErrors((prev) => {
              const updated = { ...prev };
              delete updated[index];
              return updated;
            });
          }
        }
      });
      // Only update if there are errors (to avoid unnecessary re-renders)
      if (Object.keys(errors).length > 0) {
        setBoqLineErrors((prev) => ({ ...prev, ...errors }));
      }
    }
  }, [boqLineValidationKeys, workLevelSetting, boqLines.length]);

  const fetchCategorys = async () => {
    try {
      const { data, status } = await supplierApis.fetchSupplierCategory();
      if (status) {
        const categoryOption = data.map((item: any) => ({
          label: item.category,
          value: item.id.toString(),
        }));
        setCategoryOptions(categoryOption);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const fetchMaterials = async () => {
    try {
      const { data, status } = await materialsApis.fetchProduct();
      if (status) {
        setMaterialCategoryOptions(data);
        const materialOption = data.map((item: any) => ({
          label: item.productName,
          value: item.materialID.toString(),
        }));
        setMaterialOptions(materialOption);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const loadLocationOptions = useCallback(
    async (siteIdParam?: string | number) => {
      const resolvedSiteId = siteIdParam ?? siteId;
      if (!resolvedSiteId) {
        setLocationOptions([]);
        return;
      }
      try {
        const response = await sitebreakdownMasterApis.fetchLocationsBySiteId({
          siteId: resolvedSiteId,
        });
        if (response?.status && Array.isArray(response.data)) {
          setLocationOptions(
            response.data.map((loc: any) => ({
              label: loc.locationName,
              value: loc.id?.toString(),
            }))
          );
        } else {
          setLocationOptions([]);
        }
      } catch (error) {
        console.log(error);
        setLocationOptions([]);
      }
    },
    [siteId]
  );

  useEffect(() => {
    fetchCategorys();
    fetchMaterials();
  }, []);

  useEffect(() => {
    if (siteId) {
      loadLocationOptions(siteId);
      // Load tower options for display in table
      sitebreakdownMasterApis
        .fetchUnitsBySiteId({ siteId })
        .then((response) => {
          if (response?.status && Array.isArray(response.data)) {
            setTowerOptions(
              response.data.map((unit: any) => ({
                label: unit.unitName,
                value: unit.id?.toString(),
              }))
            );
          }
        });
    }
  }, [siteId, loadLocationOptions]);

  const handleAddMaterial = (boqLineIndex?: number) => {
    setCurrentEditMaterial(null);
    setSelectedBOQLineIndex(boqLineIndex ?? null);
    setShowAddMaterialDrawer(true);
  };

  const handleEditMaterial = (boqLineIndex: number, materialIndex: number) => {
    const boqLine = getValues(`boqLines.${boqLineIndex}`);
    const material = boqLine?.materials?.[materialIndex];
    setCurrentEditMaterial({ ...material, boqLineIndex, materialIndex });
    setSelectedBOQLineIndex(boqLineIndex);
    setShowAddMaterialDrawer(true);
  };

  const handleDeleteMaterial = (
    boqLineIndex: number,
    materialIndex: number
  ) => {
    const boqLine = getValues(`boqLines.${boqLineIndex}`);
    const updatedMaterials = boqLine.materials.filter(
      (_: any, index: number) => index !== materialIndex
    );
    setValue(`boqLines.${boqLineIndex}.materials`, updatedMaterials);
  };

  const handleDeleteBOQLine = (index: number) => {
    removeBOQLine(index);
  };

  const handleEditBOQLine = (index: number) => {
    const boqLine =
      getValues(`boqLines.${index}`) || watch(`boqLines.${index}`);
    setCurrentEditBOQLine({ ...boqLine, index });
    setShowAddBOQLineModal(true);
  };

  const handleAddBOQLine = (boqLineData: any) => {
    const isEditMode = currentEditBOQLine !== null;
    const editIndex = currentEditBOQLine?.index;

    // Check for duplicate BOQ Line Name
    const existingBoqLines = getValues("boqLines") || [];
    const workLevelSetting = watch("workLevelSetting");
    let isDuplicate = false;
    let errorMessage = "";

    // Check duplicate BOQ Line Name
    const duplicateName = existingBoqLines.some(
      (existingLine: any, idx: number) => {
        if (isEditMode && idx === editIndex) return false;
        return (
          existingLine.boqLineName?.trim()?.toLowerCase() ===
          boqLineData.boqLineName?.trim()?.toLowerCase()
        );
      }
    );
    if (duplicateName) {
      Swal.fire({
        icon: "error",
        title: "Duplicate BOQ Line Name",
        text: "A BOQ line with the same name already exists. Please use a different name.",
      });
      return;
    }

    if (workLevelSetting === "TOWER_BLOCK" || workLevelSetting === "VILLA") {
      // For Tower/Block or Villa: Check only siteStructureUnitId
      if (boqLineData.siteStructureUnitId) {
        isDuplicate = existingBoqLines.some(
          (existingLine: any, idx: number) => {
            // Exclude current line if editing
            if (isEditMode && idx === editIndex) return false;
            return (
              existingLine.siteStructureUnitId ===
              boqLineData.siteStructureUnitId
            );
          }
        );
        if (isDuplicate) {
          errorMessage =
            "Each tower/villa can have only one BOQ line. Please select a different tower/villa.";
        }
      }
    } else if (workLevelSetting === "FLOOR") {
      // For Floor: Check siteStructureUnitId + floorNo
      if (boqLineData.siteStructureUnitId && boqLineData.floorNo) {
        isDuplicate = existingBoqLines.some(
          (existingLine: any, idx: number) => {
            // Exclude current line if editing
            if (isEditMode && idx === editIndex) return false;
            return (
              existingLine.siteStructureUnitId ===
                boqLineData.siteStructureUnitId &&
              existingLine.floorNo === boqLineData.floorNo
            );
          }
        );
        if (isDuplicate) {
          errorMessage =
            "Each floor (Tower/Block + Floor) can have only one BOQ line. Please select a different floor combination.";
        }
      }
    } else if (workLevelSetting === "FLAT") {
      // For Flat: Check siteStructureUnitId + floorNo + siteStructureSubUnitId
      if (
        boqLineData.siteStructureUnitId &&
        boqLineData.floorNo &&
        boqLineData.siteStructureSubUnitId
      ) {
        isDuplicate = existingBoqLines.some(
          (existingLine: any, idx: number) => {
            // Exclude current line if editing
            if (isEditMode && idx === editIndex) return false;
            return (
              existingLine.siteStructureUnitId ===
                boqLineData.siteStructureUnitId &&
              existingLine.floorNo === boqLineData.floorNo &&
              existingLine.siteStructureSubUnitId ===
                boqLineData.siteStructureSubUnitId
            );
          }
        );
        if (isDuplicate) {
          errorMessage =
            "Each flat (Tower/Block + Floor + Flat) can have only one BOQ line. Please select a different flat combination.";
        }
      }
    }

    if (isDuplicate) {
      Swal.fire({
        icon: "error",
        title: "Duplicate BOQ Line",
        text: errorMessage,
      });
      return;
    }

    if (isEditMode && editIndex !== undefined) {
      // Edit existing BOQ Line
      const existingLine =
        getValues(`boqLines.${editIndex}`) || watch(`boqLines.${editIndex}`);
      const updatedBOQLine = {
        ...existingLine,
        ...boqLineData,
        materials: existingLine?.materials || boqLineData.materials || [],
        id: existingLine?.id || currentEditBOQLine?.id,
      };

      setValue(`boqLines.${editIndex}`, updatedBOQLine, {
        shouldDirty: true,
        shouldValidate: false,
        shouldTouch: true,
      });

      // Validate the updated BOQ line
      setTimeout(() => {
        const errors = validateBoqLine(updatedBOQLine, editIndex);
        if (errors.length === 0) {
          setBoqLineErrors((prev) => {
            const updated = { ...prev };
            delete updated[editIndex];
            return updated;
          });
        } else {
          setBoqLineErrors((prev) => ({
            ...prev,
            [editIndex]: errors,
          }));
        }
      }, 100);
    } else {
      // Add new BOQ Line
      const newBOQLine = {
        ...boqLineData,
        id: `boqLine-${Date.now()}-${Math.random()}`,
        materials: boqLineData.materials || [],
      };

      const newIndex = boqLines.length;
      appendBOQLine(newBOQLine);

      // Validate the new BOQ line and clear errors if valid
      setTimeout(() => {
        const errors = validateBoqLine(newBOQLine, newIndex);
        if (errors.length === 0) {
          setBoqLineErrors((prev) => {
            const updated = { ...prev };
            delete updated[newIndex];
            return updated;
          });
        } else {
          setBoqLineErrors((prev) => ({
            ...prev,
            [newIndex]: errors,
          }));
        }
      }, 100);
    }

    // Clear edit state
    setCurrentEditBOQLine(null);
  };

  // Auto-expand newly added BOQ Line and collapse previous ones
  const prevBoqLinesLength = useRef(boqLines.length);
  useEffect(() => {
    if (boqLines.length > prevBoqLinesLength.current) {
      // New BOQ Line was added
      const lastIndex = boqLines.length - 1;
      const lastBOQLine = boqLines[lastIndex];
      const lastKey = lastBOQLine?.id?.toString() || `boqLine-${lastIndex}`;

      setTimeout(() => {
        // Collapse all previous BOQ Lines and expand only the new one
        setExpandedRowKeys([lastKey]);
      }, 100);
    }
    prevBoqLinesLength.current = boqLines.length;
  }, [boqLines.length]);

  const handleSaveMaterial = (materialData: any) => {
    if (selectedBOQLineIndex === null) {
      Swal.fire({
        icon: "warning",
        title: "BOQ Line Required",
        text: "Please select a BOQ Line to add materials",
      });
      return;
    }

    // Add material to existing BOQ Line
    const boqLine = getValues(`boqLines.${selectedBOQLineIndex}`);
    const existingMaterials = boqLine?.materials || [];

    // Check if material already exists in this BOQ Line
    if (
      currentEditMaterial &&
      currentEditMaterial.materialIndex !== undefined
    ) {
      // Editing existing material - check if materialId changed to a duplicate
      const originalMaterial =
        existingMaterials[currentEditMaterial.materialIndex];
      const isMaterialIdChanged =
        originalMaterial?.materialId !== materialData.materialId;

      if (isMaterialIdChanged) {
        // Material ID changed, check for duplicate
        const isDuplicate = existingMaterials.some(
          (m: any, index: number) =>
            index !== currentEditMaterial.materialIndex &&
            m.materialId === materialData.materialId
        );

        if (isDuplicate) {
          Swal.fire({
            icon: "error",
            title: "Error!",
            text: "This material already exists in this BOQ Line. Please select a different material.",
          });
          return;
        }
      }

      // Update the material
      const updatedMaterials = [...existingMaterials];
      updatedMaterials[currentEditMaterial.materialIndex] = materialData;
      setValue(`boqLines.${selectedBOQLineIndex}.materials`, updatedMaterials, {
        shouldDirty: true,
        shouldValidate: false,
      });
    } else {
      // Adding new material - check for duplicate
      const isDuplicate = existingMaterials.some(
        (m: any) => m.materialId === materialData.materialId
      );
      if (isDuplicate) {
        Swal.fire({
          icon: "error",
          title: "Error!",
          text: "This material already exists in this BOQ Line. Please select a different material.",
        });
        return;
      }
      // Add new material
      setValue(
        `boqLines.${selectedBOQLineIndex}.materials`,
        [...existingMaterials, materialData],
        {
          shouldDirty: true,
          shouldValidate: false,
        }
      );
    }
    setShowAddMaterialDrawer(false);
    setCurrentEditMaterial(null);
    setSelectedBOQLineIndex(null);
  };

  const handleExpand = (expanded: boolean, record: any) => {
    if (expanded) {
      setExpandedRowKeys((prevKeys) => [...prevKeys, record.key]);
    } else {
      setExpandedRowKeys((prevKeys) =>
        prevKeys.filter((key) => key !== record.key)
      );
    }
  };

  const handleCloseDrawer = () => {
    setShowAddMaterialDrawer(false);
    setCurrentEditMaterial(null);
    setSelectedBOQLineIndex(null);
  };

  const submitForm = (data: any) => {
    // Check if ALL BOQ Lines have at least one material
    if (boqLines.length === 0) {
      Swal.fire({
        icon: "warning",
        title: "BOQ Lines Required",
        text: "Please add at least one BOQ Line before proceeding.",
      });
      return;
    }

    // Validate all BOQ lines for required fields based on hierarchy
    const allErrors: Record<number, string[]> = {};
    for (let idx = 0; idx < boqLines.length; idx++) {
      const boqLine = watch(`boqLines.${idx}`);
      const lineErrors = validateBoqLine(boqLine, idx);
      if (lineErrors.length > 0) {
        allErrors[idx] = lineErrors;
      }

      if (!boqLine?.materials || boqLine.materials.length === 0) {
        if (!allErrors[idx]) {
          allErrors[idx] = [];
        }
        allErrors[idx].push("At least one material is required");
      }
    }

    // Update errors state
    setBoqLineErrors(allErrors);

    // Check if there are any errors
    if (Object.keys(allErrors).length > 0) {
      const errorMessages = Object.entries(allErrors)
        .map(
          ([index, errs]) => `BOQ Line ${Number(index) + 1}: ${errs.join(", ")}`
        )
        .join("\n");

      Swal.fire({
        icon: "error",
        title: "Validation Error",
        html: `Please fix the following errors before proceeding:<br/><br/>${Object.entries(
          allErrors
        )
          .map(
            ([index, errs]) =>
              `BOQ Line ${Number(index) + 1}: ${errs.join(", ")}`
          )
          .join("<br/>")}`,
        confirmButtonText: "OK",
      });
      return;
    }

    props.next();
  };

  const columns = useMemo(() => {
    return [
      {
        title: "BOQ Line Name",
        dataIndex: "boqLineName",
        key: "boqLineName",
        fixed: "left" as const,
        width: 200,
        render: (text: any, record: any) => {
          const errors = boqLineErrors[record.index] || [];
          return (
            <div className="d-flex align-items-center justify-content-between pe-2">
              <span>{text || "--"}</span>
              {errors.length > 0 && (
                <Popover
                  placement="left"
                  content={
                    <div>
                      <p>{errors.join(", ")}</p>
                    </div>
                  }
                  title={<span style={{ color: "red" }}>Failed</span>}
                >
                  <Badge
                    color="outline-danger"
                    placement="right"
                    style={{ cursor: "pointer" }}
                  >
                    <span>Error</span>
                    <Icon name="info" />
                  </Badge>
                </Popover>
              )}
            </div>
          );
        },
      },
      {
        title: "Contractor",
        dataIndex: "contractorName",
        key: "contractorName",
        width: 180,
        render: (text: any) => text || "--",
      },
      {
        title: workLevelSetting === "VILLA" ? "Villa" : "Tower / Block",
        dataIndex: "siteStructureUnitName",
        key: "siteStructureUnitName",
        width: 150,
        render: (text: any, record: any) => {
          // If workLevelSetting is null/empty, show "--"
          if (!workLevelSetting) {
            return "--";
          }
          // First try to use the name from record if available
          if (record.siteStructureUnitName) {
            return record.siteStructureUnitName;
          }
          // Otherwise, look it up from towerOptions
          if (record.siteStructureUnitId) {
            const unit = towerOptions.find(
              (item) => item.value === record.siteStructureUnitId?.toString()
            );
            return unit?.label || "--";
          }
          return "--";
        },
      },
      {
        title: "Floor",
        dataIndex: "floorNo",
        key: "floorNo",
        width: 100,
        render: (text: any) => {
          // Show "--" if workLevelSetting is null/empty or Floor is not applicable
          if (
            !workLevelSetting ||
            (workLevelSetting !== "FLOOR" && workLevelSetting !== "FLAT")
          ) {
            return "--";
          }
          return text || "--";
        },
      },
      {
        title: "Flat",
        dataIndex: "siteStructureSubUnitName",
        key: "siteStructureSubUnitName",
        width: 150,
        render: (text: any, record: any) => {
          // Show "--" if workLevelSetting is null/empty or Flat is not applicable
          if (!workLevelSetting || workLevelSetting !== "FLAT") {
            return "--";
          }
          // First try to use the name from record if available
          if (record.siteStructureSubUnitName) {
            return record.siteStructureSubUnitName;
          }
          // Otherwise, look it up from flatOptions
          if (record.siteStructureSubUnitId) {
            const flat = flatOptions.find(
              (item) => item.value === record.siteStructureSubUnitId?.toString()
            );
            return flat?.label || "--";
          }
          return "--";
        },
      },
      {
        title: "Location",
        dataIndex: "locationName",
        key: "locationName",
        width: 150,
        render: (text: any, record: any) => {
          // First try to use the name from record if available
          if (record.locationName) {
            return record.locationName;
          }
          // Otherwise, look it up from locationOptions
          if (record.locationId) {
            const location = locationOptions.find(
              (item) => item.value === record.locationId?.toString()
            );
            return location?.label || "--";
          }
          return "--";
        },
      },
      {
        title: "Actions",
        key: "actions",
        fixed: "right" as const,
        width: 120,
        render: (_: any, record: any, index: number) => (
          <Space size="middle">
            <a
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleEditBOQLine(index);
              }}
              title="Edit"
            >
              <EditOutlined />
            </a>
            <a
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleDeleteBOQLine(index);
              }}
              className="delete"
              title="Delete"
            >
              <DeleteOutlined />
            </a>
          </Space>
        ),
      },
    ];
  }, [
    workLevelSetting,
    boqLineErrors,
    towerOptions,
    flatOptions,
    locationOptions,
  ]);

  // Watch each BOQ Line's materials to trigger updates
  const materialCounts = boqLines.map((_, index) => {
    const boqLine = watch(`boqLines.${index}`);
    return boqLine?.materials?.length || 0;
  });

  // Watch all materials data to trigger table updates when materials change
  const allMaterialsData = boqLines.map((_, index) => {
    const boqLine = watch(`boqLines.${index}`);
    const materials = boqLine?.materials || [];
    // Create a string representation of materials to detect changes
    return materials
      .map(
        (m: any) =>
          `${m.materialId}-${m.materialName}-${m.quantitySet}-${m.categoryName}`
      )
      .join("|");
  });

  // Watch BOQ line properties to trigger table updates when BOQ line fields change
  const boqLineProperties = boqLines.map((_, index) => {
    const boqLine = watch(`boqLines.${index}`);
    return `${boqLine?.boqLineName || ""}-${
      boqLine?.siteStructureUnitId || ""
    }-${boqLine?.floorNo || ""}-${boqLine?.siteStructureSubUnitId || ""}-${
      boqLine?.locationId || ""
    }`;
  });

  // Watch the last BOQ line's materials to update canAddBOQLine
  const lastBOQLineMaterials =
    boqLines.length > 0
      ? watch(`boqLines.${boqLines.length - 1}.materials`)
      : null;

  const dataSource = useMemo(() => {
    return boqLines.map((field: any, index: number) => {
      // Get the form value for this BOQ Line
      const boqLineValue =
        watch(`boqLines.${index}`) ||
        getValues(`boqLines.${index}`) ||
        field ||
        {};
      // Use field.id if available, otherwise use boqLineValue.id, or generate one
      const rowKey =
        field.id?.toString() ||
        boqLineValue?.id?.toString() ||
        `boqLine-${index}`;

      return {
        key: rowKey,
        index,
        ...field,
        ...boqLineValue, // Spread watched values last to ensure latest values override
        // Ensure materials are included from watched value
        materials: boqLineValue.materials || field.materials || [],
        // Ensure we have the boqLineName from either source
        boqLineName: boqLineValue.boqLineName || field.boqLineName || "",
        // Ensure we have the latest IDs and values
        siteStructureUnitId:
          boqLineValue.siteStructureUnitId || field.siteStructureUnitId,
        floorNo: boqLineValue.floorNo || field.floorNo,
        siteStructureSubUnitId:
          boqLineValue.siteStructureSubUnitId || field.siteStructureSubUnitId,
        locationId: boqLineValue.locationId || field.locationId,
      };
    });
  }, [
    boqLines.length,
    materialCounts.join(","),
    allMaterialsData.join("||"),
    boqLineProperties.join("||"),
    watch,
    getValues,
  ]);

  // Check if the last BOQ Line has materials - enable Add BOQ Line only if it does
  const canAddBOQLine = useMemo(() => {
    if (boqLines.length === 0) {
      return true; // Can add first BOQ Line
    }
    const lastIndex = boqLines.length - 1;
    const lastBOQLine =
      watch(`boqLines.${lastIndex}`) ||
      getValues(`boqLines.${lastIndex}`) ||
      boqLines[lastIndex];
    const hasMaterials =
      (lastBOQLine?.materials && lastBOQLine.materials.length > 0) ||
      (lastBOQLineMaterials && lastBOQLineMaterials.length > 0);
    return hasMaterials;
  }, [
    boqLines.length,
    boqLines,
    materialCounts.join(","),
    lastBOQLineMaterials,
    watch,
    getValues,
  ]);

  return (
    <>
      <style>
        {`
          .error-tableRow {
            background-color: #fff1f0 !important;
          }
          .error-tableRow:hover {
            background-color: #ffe7e6 !important;
          }
          .error-tableRow td {
            background-color: transparent !important;
          }
        `}
      </style>
      <form className="content clearfix" onSubmit={handleSubmit(submitForm)}>
        {/* BOQ Lines Table with Expandable Materials */}
        <div style={{ marginBottom: 20 }}>
          <Table
            columns={columns}
            dataSource={dataSource}
            scroll={{ x: 1000 }}
            rowClassName={(record: any) => {
              const errors = boqLineErrors[record.index] || [];
              return errors.length > 0 ? "error-tableRow" : "";
            }}
            expandable={{
              expandedRowRender: (record: any) => {
                // Get fresh materials data from form to ensure latest values
                const boqLineData =
                  watch(`boqLines.${record.index}`) ||
                  getValues(`boqLines.${record.index}`) ||
                  record;
                const materials =
                  boqLineData.materials || record.materials || [];
                return (
                  <div>
                    <p
                      style={{
                        margin: "0 0 10px 0",
                        fontWeight: "bold",
                      }}
                    >
                      Materials
                    </p>
                    {materials.length === 0 ? (
                      <div
                        style={{
                          padding: "10px",
                          textAlign: "center",
                          color: "#999",
                        }}
                      >
                        <p>
                          No materials added yet. Click "Add Material" to add
                          materials.
                        </p>
                      </div>
                    ) : (
                      <div className="mt-3">
                        <Table
                          className="customTable"
                          columns={[
                            {
                              title: "#",
                              key: "index",
                              width: 50,
                              render: (_: any, __: any, index: number) =>
                                index + 1,
                            },
                            {
                              title: "Category",
                              dataIndex: "categoryName",
                              key: "categoryName",
                              width: 150,
                            },
                            {
                              title: "Material Name",
                              dataIndex: "materialName",
                              key: "materialName",
                              width: 200,
                            },
                            {
                              title: "Standard Qty",
                              dataIndex: "quantitySet",
                              key: "quantitySet",
                              width: 100,
                            },
                            {
                              title: "UOM",
                              dataIndex: "uomName",
                              key: "uomName",
                              width: 100,
                            },
                            {
                              title: "Standard Price",
                              dataIndex: "standardPrice",
                              key: "standardPrice",
                              width: 120,
                              render: (text: any) => text || "--",
                            },
                            {
                              title: "Actions",
                              key: "actions",
                              width: 100,
                              render: (
                                _: any,
                                material: any,
                                materialIndex: number
                              ) => (
                                <Space size="middle">
                                  <a
                                    onClick={() =>
                                      handleEditMaterial(
                                        record.index,
                                        materialIndex
                                      )
                                    }
                                  >
                                    <EditOutlined />
                                  </a>
                                  <a
                                    onClick={() =>
                                      handleDeleteMaterial(
                                        record.index,
                                        materialIndex
                                      )
                                    }
                                    className="delete"
                                  >
                                    <DeleteOutlined />
                                  </a>
                                </Space>
                              ),
                            },
                          ]}
                          dataSource={materials.map(
                            (material: any, index: number) => ({
                              ...material,
                              key: index,
                            })
                          )}
                          scroll={{ x: 800 }}
                          bordered
                          size="small"
                          pagination={false}
                        />
                      </div>
                    )}
                    <div
                      style={{
                        marginTop: 10,
                        display: "flex",
                        justifyContent: "flex-end",
                        alignItems: "center",
                      }}
                    >
                      <span
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          handleAddMaterial(record.index);
                        }}
                        style={{
                          cursor: "pointer",
                          fontSize: 14,
                          color: "#2263b3",
                        }}
                      >
                        <Icon name="plus-round"></Icon> &nbsp; Add Material
                      </span>
                    </div>
                  </div>
                );
              },
              expandedRowKeys,
              onExpand: handleExpand,
              rowExpandable: () => true,
            }}
            bordered
            size="small"
            pagination={false}
            locale={{
              emptyText: "Please click 'Add BOQ Line' to add BOQ lines.",
            }}
          />
        </div>

        {/* Add BOQ Line Button - below table like Budget */}
        {/* Hide button only if site hierarchy is NOT selected AND one BOQ line exists */}
        {!(!workLevelSetting && boqLines.length > 0) && (
          <div
            style={{
              marginTop: 10,
              display: "flex",
              gap: "15px",
              alignItems: "center",
            }}
          >
            <span
              onClick={() => {
                if (canAddBOQLine) {
                  setCurrentEditBOQLine(null);
                  setShowAddBOQLineModal(true);
                }
              }}
              style={{
                cursor: canAddBOQLine ? "pointer" : "not-allowed",
                fontSize: 14,
                color: canAddBOQLine ? "#2263b3" : "#999",
                opacity: canAddBOQLine ? 1 : 0.6,
              }}
            >
              <Icon name="plus-round"></Icon> &nbsp; Add BOQ Line
            </span>
          </div>
        )}

        {/* Navigation Buttons */}
        <div className="actions clearfix">
          <ul>
            <li>
              <Button
                color="primary"
                type="submit"
                disabled={
                  boqLines.length === 0 ||
                  !boqLines.every((_, idx) => {
                    const line = watch(`boqLines.${idx}`);
                    return line?.materials && line.materials.length > 0;
                  }) ||
                  Object.keys(boqLineErrors).length > 0
                }
              >
                Next
              </Button>
            </li>
            <li>
              <Button color="primary" onClick={props.prev}>
                Previous
              </Button>
            </li>
          </ul>
        </div>
      </form>

      {/* Add BOQ Line Modal - Outside form to prevent bubbling */}
      <AddBOQLineModal
        isOpen={showAddBOQLineModal}
        onClose={() => {
          setShowAddBOQLineModal(false);
          setCurrentEditBOQLine(null);
        }}
        onSubmit={handleAddBOQLine}
        workLevelSetting={workLevelSetting}
        siteId={siteId}
        editData={currentEditBOQLine}
        existingBoqLines={getValues("boqLines") || []}
      />

      {/* Drawer for Adding/Editing Materials - Outside form to prevent bubbling */}
      <Drawer
        title={currentEditMaterial ? "Edit Material" : "Add Material"}
        placement="right"
        onClose={handleCloseDrawer}
        open={showAddMaterialDrawer}
        width={1000}
        destroyOnClose={true}
      >
        <BOQMaterialDrawer
          materialCategoryOptions={materialCategoryOptions}
          categoryOptions={categoryOptions}
          materialOptions={materialOptions}
          blockArea={blockArea}
          currentMaterial={currentEditMaterial}
          onSave={handleSaveMaterial}
          onCancel={handleCloseDrawer}
          setValue={setValue}
          watch={watch}
          selectedBOQLineIndex={selectedBOQLineIndex}
          boqLines={boqLines}
          getValues={getValues}
        />
      </Drawer>
    </>
  );
};

export default ConsumptionTarget_Materails;
